/**
 * Blood Moon scene manifest — the parallax "sets" for each phase of the game.
 *
 * Every scene ships with `layers: []` for now, so SceneStack renders each scene's
 * `fallbackGradient` (lifted verbatim from theme/phaseTheme.ts) — the game looks
 * exactly like today until art lands, then upgrades with zero screen changes.
 *
 * ── ART CONTRACT (for the asset agent) ────────────────────────────────────────
 * Scene art lands at these stable paths under this directory
 * (packages/client/src/assets/scenes/bloodmoon/). Each is an authored webp,
 * ~120% of the design width so its `drift` loops seamlessly. When a file exists,
 * uncomment its import + push it into the scene's `layers` array with the depth /
 * slot / drift / pulse noted beside each import below. Glow layers ('screen'
 * blend) sit above their base layer.
 *
 *   night-sky.webp         sky   depth .00  (crimson-black star field)
 *   night-moon.webp        sky   depth .02  blend:screen  pulse (blood-moon glow)
 *   night-treeline.webp    mid   depth .10  drift ±40px / 90s
 *   night-fog.webp         fog   depth .05  blend:screen  drift ±80px / 60s
 *   night-fore-brush.webp  fore  depth .18  drift ±20px / 120s
 *   dawn-sky.webp          sky   depth .00  (sky bleeding to first light)
 *   dawn-treeline.webp     mid   depth .10
 *   dawn-fog.webp          fog   depth .05  blend:screen
 *   day-sky.webp           sky   depth .00  (overcast village daylight)
 *   day-rooftops.webp      mid   depth .10
 *   day-fore-fence.webp    fore  depth .18
 *   vote-gallows.webp      mid   depth .10  (the square + gallows silhouette)
 *   vote-sky.webp          sky   depth .00
 *   vote-fog.webp          fog   depth .05  blend:screen
 *   lobby-sky.webp         sky   depth .00  (warm ember-lit tent interior)
 *   lobby-banners.webp     fore  depth .18  drift ±16px / 100s
 *   end-village-sky.webp   sky   depth .00  (gold sunrise — village victory)
 *   end-wolves-sky.webp    sky   depth .00  (blood-drenched — wolves victory)
 * ──────────────────────────────────────────────────────────────────────────────
 */

import nightSky from './bloodmoon/night-sky.webp';
import nightMoon from './bloodmoon/night-moon.webp';
import nightTreeline from './bloodmoon/night-treeline.webp';
import nightFog from './bloodmoon/night-fog.webp';
import nightForeBrush from './bloodmoon/night-fore-brush.webp';
import dawnSky from './bloodmoon/dawn-sky.webp';
import dawnTreeline from './bloodmoon/dawn-treeline.webp';
import dawnFog from './bloodmoon/dawn-fog.webp';
import daySky from './bloodmoon/day-sky.webp';
import dayRooftops from './bloodmoon/day-rooftops.webp';
import dayForeFence from './bloodmoon/day-fore-fence.webp';
import voteSky from './bloodmoon/vote-sky.webp';
import voteGallows from './bloodmoon/vote-gallows.webp';
import voteFog from './bloodmoon/vote-fog.webp';
import lobbySky from './bloodmoon/lobby-sky.webp';
import lobbyBanners from './bloodmoon/lobby-banners.webp';
import endVillageSky from './bloodmoon/end-village-sky.webp';
import endWolvesSky from './bloodmoon/end-wolves-sky.webp';

import type { SceneManifest } from './types.js';

// Fallback gradients — copied verbatim from theme/phaseTheme.ts so a zero-layer
// scene is pixel-identical to today's PhaseBackground for that phase.
const GRAD_NIGHT = 'linear-gradient(180deg, #080808 0%, #320001 100%)';
const GRAD_DAWN = 'linear-gradient(180deg, #080808 0%, #0d0507 100%)';
const GRAD_WARM = 'linear-gradient(180deg, #080808 0%, #1a0f08 100%)';
const GRAD_NEUTRAL = 'linear-gradient(180deg, #080808 0%, #140002 100%)';
const GRAD_WOLF_WIN = 'linear-gradient(180deg, #080808 0%, #320001 100%)';
const GRAD_VILLAGE_WIN = 'linear-gradient(180deg, #080808 0%, #1a1000 100%)';

export const bloodmoonScenes: SceneManifest = {
  gameId: 'bloodmoon',
  scenes: {
    LOBBY: {
      layers: [
        { src: lobbySky, depth: 0, slot: 'sky' },
        // Drift sped ~1.5× vs. authored (100s → 67s) — lobby felt too static.
        { src: lobbyBanners, depth: 0.18, slot: 'fore', drift: { xPx: 16, periodS: 67 } },
      ],
      particles: [{ preset: 'goldDust', intensity: 0.4, slot: 'fog' }],
      accent: '#c9923f', // --gold-4
      fallbackGradient: GRAD_WARM,
    },
    NIGHT: {
      layers: [
        { src: nightSky, depth: 0, slot: 'sky' },
        { src: nightMoon, depth: 0.02, slot: 'sky', blend: 'screen',
          pulse: { opacity: [0.7, 1, 0.7], periodS: 8 } },
        // Drift sped ~1.5× vs. authored (90/60/120s → 60/40/80s) — night felt too static.
        { src: nightTreeline, depth: 0.1, slot: 'mid', drift: { xPx: 40, periodS: 60 } },
        { src: nightFog, depth: 0.05, slot: 'fog', blend: 'screen', drift: { xPx: 80, periodS: 40 } },
        { src: nightForeBrush, depth: 0.18, slot: 'fore', drift: { xPx: 20, periodS: 80 } },
      ],
      particles: [
        { preset: 'fog', intensity: 0.6, slot: 'fog' },
        { preset: 'bloodMotes', intensity: 0.5, slot: 'fore' },
      ],
      accent: '#990000', // --blood-4
      fallbackGradient: GRAD_NIGHT,
    },
    DAWN: {
      layers: [
        { src: dawnSky, depth: 0, slot: 'sky' },
        { src: dawnTreeline, depth: 0.1, slot: 'mid' },
        { src: dawnFog, depth: 0.05, slot: 'fog', blend: 'screen' },
      ],
      particles: [{ preset: 'fog', intensity: 0.4, slot: 'fog' }],
      accent: '#c1663c', // --dawn-3
      fallbackGradient: GRAD_DAWN,
    },
    DAY: {
      layers: [
        { src: daySky, depth: 0, slot: 'sky' },
        { src: dayRooftops, depth: 0.1, slot: 'mid' },
        { src: dayForeFence, depth: 0.18, slot: 'fore' },
      ],
      particles: [{ preset: 'goldDust', intensity: 0.3, slot: 'fog' }],
      accent: '#c9923f', // --gold-4
      fallbackGradient: GRAD_WARM,
    },
    VOTE: {
      layers: [
        { src: voteSky, depth: 0, slot: 'sky' },
        { src: voteGallows, depth: 0.1, slot: 'mid' },
        { src: voteFog, depth: 0.05, slot: 'fog', blend: 'screen' },
      ],
      particles: [{ preset: 'ash', intensity: 0.4, slot: 'fore' }],
      accent: '#802636', // --blood-3
      fallbackGradient: GRAD_NEUTRAL,
    },
    END_VILLAGE: {
      layers: [
        { src: endVillageSky, depth: 0, slot: 'sky' },
      ],
      particles: [{ preset: 'goldDust', intensity: 0.7, slot: 'fog' }],
      accent: '#c9a44e',
      fallbackGradient: GRAD_VILLAGE_WIN,
    },
    END_WOLVES: {
      layers: [
        { src: endWolvesSky, depth: 0, slot: 'sky' },
      ],
      particles: [{ preset: 'bloodMotes', intensity: 0.8, slot: 'fore' }],
      accent: '#990000',
      fallbackGradient: GRAD_WOLF_WIN,
    },
  },
  // Phase → scene. Transient / interlude night phases all share the NIGHT set;
  // END defaults to the village ending and is overridden to END_WOLVES at runtime
  // by SceneStack when winner === 'WOLVES'.
  phaseToScene: {
    LOBBY: 'LOBBY',
    DEAL: 'NIGHT',
    NIGHT_INTRO: 'NIGHT',
    NIGHT_WOLVES: 'NIGHT',
    NIGHT_INTERLUDE: 'NIGHT',
    NIGHT_SEER: 'NIGHT',
    RESOLVE_NIGHT: 'NIGHT',
    DAWN: 'DAWN',
    DISCUSS: 'DAY',
    VOTE: 'VOTE',
    RESOLVE_VOTE: 'VOTE',
    END: 'END_VILLAGE',
    // The Whisper phases never render through this (Blood Moon) manifest — they
    // are here only to satisfy the total Record<Phase, string>. Mapped to existing
    // Blood Moon scenes as inert placeholders; the Whisper manifest (M6) maps them
    // to arcana scenes. TODO(whisper M6).
    WHISPER_INTRO: 'NIGHT',
    QUESTION: 'NIGHT',
    RESOLVE_GUESS: 'NIGHT',
    HUNT: 'VOTE',
    RESOLVE_HUNT: 'VOTE',
    SCORE: 'END_VILLAGE',
    // One Night phases never render through this (Blood Moon) manifest either —
    // present only to satisfy the total Record<Phase, string>. Mapped onto the
    // closest existing Blood Moon scene as inert placeholders. TODO(onenight):
    // a dedicated One Night scene manifest if the game gets bespoke art.
    ONENIGHT_NIGHTFALL: 'NIGHT',
    ONENIGHT_NIGHT_ACTIONS: 'NIGHT',
    ONENIGHT_DAWN: 'DAWN',
    ONENIGHT_DISCUSS: 'DAY',
    ONENIGHT_VOTE: 'VOTE',
    ONENIGHT_RESOLVE: 'VOTE',
  },
  // Dim the scene stack against the ink-black base so the art sits behind the
  // ornaments/UI instead of dominating. LOBBY reads calmest (0.5); every gameplay
  // phase holds at 0.7. Applied by SceneStack to a wrapper containing all scene
  // layers, so 'screen' blend layers still composite correctly (see SceneStack).
  phaseDim: {
    LOBBY: 0.5,
    DEAL: 0.7,
    NIGHT_INTRO: 0.7,
    NIGHT_WOLVES: 0.7,
    NIGHT_INTERLUDE: 0.7,
    NIGHT_SEER: 0.7,
    RESOLVE_NIGHT: 0.7,
    DAWN: 0.7,
    DISCUSS: 0.7,
    VOTE: 0.7,
    RESOLVE_VOTE: 0.7,
    END: 0.7,
  },
};
